import { BrandMark } from '../components/BrandMark';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import {
  useSubscription,
  useBillingConfig,
  useCancelSubscription,
  billingKeys,
} from '../hooks/useSubscription';
import { openCheckout, billingService } from '../services/billing';
import type { BillingCycle } from '../services/billing';

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past due',
  paused: 'Paused',
  canceled: 'Canceled',
};

export default function Billing() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const { data: status, isLoading: statusLoading } = useSubscription();
  const { data: config, isLoading: configLoading } = useBillingConfig();
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [manageBusy, setManageBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [manageError, setManageError] = useState<string | null>(null);
  const cancelSub = useCancelSubscription();

  const isActive = status?.active ?? false;
  const sub = status?.subscription ?? null;
  const isTrialing = sub?.status === 'trialing';

  // Paddle fires `checkout.completed` when payment finishes; the webhook then
  // grants entitlement a moment later. Poll the subscription a few times so the
  // page reflects Pro without a manual refresh.
  useEffect(() => {
    function onCompleted() {
      setNotice('Payment received — activating your Pro plan…');
      [1500, 4000, 8000, 15000, 25000].forEach((delay) =>
        setTimeout(
          () =>
            queryClient.invalidateQueries({
              queryKey: billingKeys.subscription(),
            }),
          delay
        )
      );
    }
    window.addEventListener('paddle:checkout-completed', onCompleted);
    return () =>
      window.removeEventListener('paddle:checkout-completed', onCompleted);
  }, [queryClient]);

  async function handleManageBilling() {
    setManageError(null);
    setManageBusy(true);
    try {
      const url = await billingService.getPortalUrl();
      window.location.href = url;
    } catch {
      setManageError('Could not open the billing portal. Please try again.');
      setManageBusy(false);
    }
  }

  function handleCancel() {
    setManageError(null);
    setNotice(null);
    if (
      !window.confirm(
        'Cancel your subscription? You keep Pro until the end of the current period.'
      )
    ) {
      return;
    }
    cancelSub.mutate(undefined, {
      onSuccess: (res) => {
        const when = res.effectiveAt
          ? ` on ${new Date(res.effectiveAt).toLocaleDateString()}`
          : '';
        setNotice(`Your subscription will end${when}. You keep Pro until then.`);
        // The webhook confirms the scheduled change shortly after — poll so the
        // status/label updates without a manual refresh.
        [1500, 4000, 8000].forEach((delay) =>
          setTimeout(
            () =>
              queryClient.invalidateQueries({
                queryKey: billingKeys.subscription(),
              }),
            delay
          )
        );
      },
      onError: () => setManageError('Could not cancel. Please try again.'),
    });
  }

  async function handleUpgrade(cycle: BillingCycle = 'monthly') {
    if (!user || !config) return;
    setCheckoutError(null);
    setBusy(true);
    try {
      // Entitlement refresh is driven by Paddle's `checkout.completed` event
      // (see the effect below), not by this call returning — which happens when
      // the overlay *opens*, long before payment finishes.
      await openCheckout({ config, email: user.email, userId: user.id, cycle });
    } catch (err) {
      setCheckoutError(
        err instanceof Error ? err.message : 'Could not open checkout'
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link
              to="/dashboard"
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Dashboard
              </Link>
              <Link
                to="/billing"
                className="text-sm font-medium text-foreground"
              >
                Billing
              </Link>
            </nav>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {user?.email}
            </span>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Billing</h2>
          <p className="text-muted-foreground">
            Manage your AgentMesh subscription.
          </p>
        </div>

        {statusLoading || configLoading ? (
          <LoadingSkeleton className="h-48" />
        ) : (
          <div className="space-y-6">
            {/* Current plan */}
            <Card>
              <CardHeader>
                <CardTitle>Current plan</CardTitle>
                <CardDescription>
                  {isActive
                    ? 'You are on the Pro plan.'
                    : 'You are on the Free plan.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">
                      {isActive ? 'Pro' : 'Free'}
                    </p>
                    {sub && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Status: {STATUS_LABEL[sub.status] ?? sub.status}
                        {sub.currentPeriodEnd && (
                          <>
                            {sub.status === 'canceled' || sub.status === 'paused'
                                ? ' · Access ends '
                                : isTrialing
                                  ? ' · Trial ends '
                                  : ' · Renews '}
                            {new Date(
                              sub.currentPeriodEnd
                            ).toLocaleDateString()}
                          </>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {isActive && (
                  <>
                    <div className="mt-5 flex flex-wrap gap-2 border-t pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleManageBilling}
                        disabled={manageBusy}
                      >
                        {manageBusy ? 'Opening…' : 'Manage billing & invoices'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={handleCancel}
                        disabled={cancelSub.isPending}
                      >
                        {cancelSub.isPending
                          ? 'Cancelling…'
                          : 'Cancel subscription'}
                      </Button>
                    </div>
                    {notice && (
                      <p className="mt-3 text-sm text-green-600">{notice}</p>
                    )}
                    {manageError && (
                      <p className="mt-3 text-sm text-red-600">{manageError}</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Upgrade */}
            {!isActive && (
              <Card>
                <CardHeader>
                  <CardTitle>AgentMesh Pro</CardTitle>
                  <CardDescription>
                    Unlock the full platform. Both plans start with a 10-day
                    trial — your card is saved at checkout and charged only when
                    the trial ends.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/*
                    The annual option renders only when Paddle actually has an
                    annual price. Showing a cycle the backend cannot check out
                    would let someone pick a plan and then hit an error at the
                    moment they decided to pay.
                  */}
                  <p className="text-3xl font-bold">
                    $29
                    <span className="text-base font-normal text-muted-foreground">
                      {' '}
                      / month
                    </span>
                  </p>
                  {config?.configured ? (
                    <div className="space-y-3">
                      <Button
                        onClick={() => handleUpgrade('monthly')}
                        disabled={busy}
                      >
                        {busy ? 'Opening checkout…' : 'Upgrade to Pro — $29/mo'}
                      </Button>
                      {config.priceIdAnnual && (
                        <div className="space-y-1">
                          <Button
                            variant="outline"
                            onClick={() => handleUpgrade('annual')}
                            disabled={busy}
                          >
                            {busy
                              ? 'Opening checkout…'
                              : 'Pay yearly — $290/yr'}
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            Two months free. Billed once a year, cancel anytime.
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Billing is not configured yet. Please check back soon.
                    </p>
                  )}
                  {checkoutError && (
                    <p className="text-sm text-red-600">{checkoutError}</p>
                  )}
                  {config?.environment === 'sandbox' && config?.configured && (
                    <p className="text-xs text-muted-foreground">
                      Sandbox mode — use a Paddle test card, no real charge.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
